import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';

interface AbsenceType {
  id: string;
  name: string;
  short_name: string;
  color: string;
  text_color?: string;
  counts_as_hours: boolean;
  default_hours?: number;
  description?: string;
  is_active: boolean;
}

const AbsenceTypesManagement: React.FC = () => {
  const [absenceTypes, setAbsenceTypes] = useState<AbsenceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    color: '#9CA3AF',
    text_color: '#FFFFFF',
    counts_as_hours: false,
    default_hours: '',
    description: '',
  });

  useEffect(() => {
    loadAbsenceTypes();
  }, []);

  const loadAbsenceTypes = async () => {
    try {
      const data = await apiFetch('/dienstplan/absence-types');
      setAbsenceTypes(data);
    } catch (error) {
      console.error('Fehler beim Laden der Abwesenheitstypen:', error);
      alert('Fehler beim Laden der Abwesenheitstypen');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.short_name) {
      alert('Name und Kurzname sind erforderlich');
      return;
    }

    try {
      const payload = {
        ...formData,
        default_hours: formData.default_hours ? parseFloat(formData.default_hours) : null,
      };

      if (editingId) {
        await apiFetch(`/dienstplan/absence-types/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch('/dienstplan/absence-types', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }

      setFormData({
        name: '',
        short_name: '',
        color: '#9CA3AF',
        text_color: '#FFFFFF',
        counts_as_hours: false,
        default_hours: '',
        description: '',
      });
      setEditingId(null);
      loadAbsenceTypes();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern des Abwesenheitstyps');
    }
  };

  const handleEdit = (type: AbsenceType) => {
    setFormData({
      name: type.name,
      short_name: type.short_name,
      color: type.color,
      text_color: type.text_color || '#FFFFFF',
      counts_as_hours: type.counts_as_hours,
      default_hours: type.default_hours?.toString() || '',
      description: type.description || '',
    });
    setEditingId(type.id);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Möchten Sie diesen Abwesenheitstyp wirklich löschen?')) {
      return;
    }

    try {
      await apiFetch(`/dienstplan/absence-types/${id}`, {
        method: 'DELETE',
      });
      loadAbsenceTypes();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen des Abwesenheitstyps');
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      short_name: '',
      color: '#9CA3AF',
      text_color: '#FFFFFF',
      counts_as_hours: false,
      default_hours: '',
      description: '',
    });
    setEditingId(null);
  };

  if (loading) {
    return <div className="p-6">Lädt...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6 dark:text-white">Abwesenheitstypen verwalten</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4 dark:text-white">
            {editingId ? 'Abwesenheitstyp bearbeiten' : 'Neuer Abwesenheitstyp'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">
                Kurzname *
              </label>
              <input
                type="text"
                value={formData.short_name}
                onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                maxLength={50}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">
                Farbe
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="h-10 w-20"
                />
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="flex-1 px-3 py-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">
                Schriftfarbe
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={formData.text_color}
                  onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                  className="h-10 w-20"
                />
                <input
                  type="text"
                  value={formData.text_color}
                  onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                  className="flex-1 px-3 py-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.counts_as_hours}
                  onChange={(e) => setFormData({ ...formData, counts_as_hours: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium dark:text-gray-300">
                  Als Stunden zählen
                </span>
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Wenn aktiviert, werden Stunden für diese Abwesenheit erfasst
              </p>
            </div>

            {formData.counts_as_hours && (
              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-300">
                  Standard-Stunden pro Tag
                </label>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  max="24"
                  value={formData.default_hours}
                  onChange={(e) => setFormData({ ...formData, default_hours: e.target.value })}
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  placeholder="z.B. 8.00"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">
                Beschreibung
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
                rows={3}
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                {editingId ? 'Aktualisieren' : 'Erstellen'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 border rounded dark:border-gray-600 dark:text-gray-300"
                >
                  Abbrechen
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4 dark:text-white">Vorhandene Abwesenheitstypen</h2>

          <div className="space-y-2">
            {absenceTypes.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">Keine Abwesenheitstypen vorhanden</p>
            ) : (
              absenceTypes.map((type) => (
                <div
                  key={type.id}
                  className="flex items-center gap-3 p-3 border rounded dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700hover:bg-gray-700"
                >
                  <div
                    className="w-8 h-8 rounded flex-shrink-0"
                    style={{ backgroundColor: type.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium dark:text-white">{type.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {type.short_name}
                      {type.counts_as_hours && (
                        <span className="ml-2">
                          • {type.default_hours ? `${type.default_hours}h` : 'mit Stunden'}
                        </span>
                      )}
                    </div>
                    {type.description && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {type.description}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(type)}
                      className="px-3 py-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(type.id)}
                      className="px-3 py-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbsenceTypesManagement;
